import { useState } from "react";
import { Music, Search, Loader2, X, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMusicSearch, usePlatformTracks, type MusicTrack } from "@/hooks/platform/useMusicSources";
import type { AttachedTrack } from "@/lib/musicTag";
import MediaImage from "./MediaImage";

/** Same chart and genre options as the Music page. */
const MUSIC_CHIPS = ["Top 50", "Amapiano", "Afrobeats", "Hip-Hop", "House", "R&B", "Gospel", "Gqom", "Jazz", "Pop"];

const toAttached = (t: MusicTrack): AttachedTrack => ({
  title: t.title, artist: t.artist, artwork: t.artwork, previewUrl: t.previewUrl, spotifyUrl: t.spotifyUrl || null, appleUrl: t.appleUrl,
});

/** "Add music" button: search the Music page catalogue (Spotify / Apple Music + platform uploads) and pick a track. */
export const MusicAttachButton = ({ value, onChange, size = "sm" }: { value: AttachedTrack | null; onChange: (t: AttachedTrack | null) => void; size?: "sm" | "default" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState("Top 50");
  const searchTerm = q.trim().length > 1 ? q : chip === "Top 50" ? "South Africa Top 50" : `${chip} South Africa`;
  const external = useMusicSearch(searchTerm, open);
  const platform = usePlatformTracks();
  // Platform uploads only appear when searched, so no song is pinned as a default.
  const local = q.trim().length > 1 ? (platform.data ?? []).filter((t) => `${t.title} ${t.artist}`.toLowerCase().includes(q.trim().toLowerCase())) : [];
  const results = [...local.slice(0, 5), ...(external.data ?? [])];

  if (value) return <MusicChip track={value} onRemove={() => onChange(null)} />;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size={size} className="rounded-full"><Music className="w-3.5 h-3.5 mr-1" />Add music</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add music</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a song or artist" className="pl-9" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] pb-1">
          {MUSIC_CHIPS.map((c) => (
            <Button key={c} type="button" size="sm" variant={!q.trim() && chip === c ? "default" : "outline"} className="rounded-full shrink-0 h-7 text-xs"
              onClick={() => { setChip(c); setQ(""); }}>{c}</Button>
          ))}
        </div>
        {external.isFetching && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Searching…</p>}
        <div className="space-y-1">
          {results.map((t) => (
            <button key={t.id} type="button" onClick={() => { onChange(toAttached(t)); setOpen(false); }}
              className="w-full flex items-center gap-3 rounded-lg p-2 text-left hover:bg-muted">
              {t.artwork ? <MediaImage src={t.artwork} alt="" className="w-10 h-10 rounded object-cover shrink-0" /> : <div className="w-10 h-10 rounded bg-muted flex items-center justify-center"><Music className="w-4 h-4" /></div>}
              <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{t.title}</p><p className="text-xs text-muted-foreground truncate">{t.artist}</p></div>
              {t.previewUrl && <span className="text-[10px] text-muted-foreground shrink-0">Preview</span>}
            </button>
          ))}
          {!external.isFetching && results.length === 0 && <p className="text-sm text-muted-foreground">No tracks found.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Compact track card shown on posts and in composers. */
export const MusicChip = ({ track, onRemove }: { track: AttachedTrack; onRemove?: () => void }) => (
  <div className="flex items-center gap-3 rounded-xl border border-border p-2 max-w-sm">
    {track.artwork ? <MediaImage src={track.artwork} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-muted flex items-center justify-center"><Music className="w-4 h-4" /></div>}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium truncate">{track.title}</p>
      <p className="text-xs text-muted-foreground truncate">{track.artist}</p>
      {track.previewUrl && <audio controls preload="none" src={track.previewUrl} className="h-7 mt-1 w-full max-w-[200px]" />}
    </div>
    {track.spotifyUrl && <a href={track.spotifyUrl} target="_blank" rel="noopener noreferrer" aria-label="Open in Spotify" className="text-muted-foreground hover:text-foreground"><ExternalLink className="w-4 h-4" /></a>}
    {onRemove && <button type="button" onClick={onRemove} aria-label="Remove music" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
  </div>
);
